import React, { CSSProperties, forwardRef, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';

import { sprinkles } from '../../tokens';
import { tokens } from '../../tokens/contract.css';
import { typography, TypographyVariant } from '../../tokens/typography.css';
import { Tooltip } from '../Tooltip';

export interface TextProps extends React.HTMLAttributes<HTMLSpanElement> {
  textStyle: TypographyVariant;
  as?: 'span' | 'p' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  children?: React.ReactNode;
  color?: keyof typeof tokens.color;
  style?: CSSProperties;
  /**
   * Apply full width(by flex: 1)
   */
  fullWidth?: boolean;
  /**
   * Enable text ellipsis overflow
   * - true: Single line ellipsis
   * - number: Multi-line ellipsis with specified line count
   */
  ellipsis?: boolean | number;
  /**
   * Show tooltip with full text when ellipsis occurs
   * Only works when ellipsis prop is enabled
   * @default false
   */
  showTooltipOnEllipsis?: boolean;
  /** 필수값인 textStyle을 무시하고 스타일 상속받기 위한 prop */
  ignoreTextStyle?: boolean;
}

// Extract text content from React children
const extractTextFromChildren = (children: React.ReactNode): string => {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join('');
  }

  if (React.isValidElement(children) && children.props.children) {
    return extractTextFromChildren(children.props.children);
  }

  return '';
};

// Generate ellipsis styles based on prop value
const getEllipsisStyles = (ellipsis: boolean | number | undefined): CSSProperties => {
  if (!ellipsis) return {};

  if (ellipsis === true) {
    // Single line ellipsis
    return {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
      maxWidth: '100%',
      flexShrink: 1,
    };
  }

  // Multi-line ellipsis
  return {
    display: '-webkit-box',
    WebkitLineClamp: ellipsis,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordBreak: 'break-word',
    minWidth: 0,
    maxWidth: '100%',
    flexShrink: 1,
  };
};

export const Text = forwardRef<HTMLElement, TextProps>(
  (
    {
      textStyle,
      fullWidth = false,
      as: Component = 'span',
      children,
      color,
      style,
      className,
      ellipsis,
      showTooltipOnEllipsis = false,
      ignoreTextStyle = false,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLElement | null>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    // Check if text is overflowing (ellipsis occurred)
    useEffect(() => {
      if (!showTooltipOnEllipsis || !ellipsis) {
        return;
      }

      const element = internalRef.current;
      if (!element) {
        return;
      }

      const checkOverflow = () => {
        if (ellipsis === true) {
          // Single line ellipsis check
          setIsOverflowing(element.scrollWidth > element.clientWidth);
        } else {
          // Multi-line ellipsis check
          setIsOverflowing(element.scrollHeight > element.clientHeight);
        }
      };

      // Initial check
      checkOverflow();

      // Use ResizeObserver to detect size changes
      const resizeObserver = new ResizeObserver(() => {
        checkOverflow();
      });

      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }, [showTooltipOnEllipsis, ellipsis, children]);

    // Determine if tooltip should be shown
    const shouldShowTooltip = showTooltipOnEllipsis && isOverflowing;
    const fullText = shouldShowTooltip ? extractTextFromChildren(children) : undefined;

    const textContent = (
      <Component
        ref={(node) => {
          // Handle both internal ref and forwarded ref
          if (node) {
            internalRef.current = node;
          }
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref && node) {
            // Type assertion to handle readonly ref
            (ref as React.MutableRefObject<HTMLElement | null>).current = node;
          }
        }}
        className={clsx(
          !ignoreTextStyle && typography[textStyle],
          sprinkles({ flex: 1 }),
          className,
        )}
        style={{
          ...(color && { color: tokens.color[color] }),
          ...getEllipsisStyles(ellipsis),
          ...style,
        }}
        {...props}
      >
        {children}
      </Component>
    );

    return (
      <Tooltip
        content={fullText ?? ''}
        disabled={!(shouldShowTooltip && fullText)}
        className={clsx(
          ellipsis ? sprinkles({ minWidth: 0 }) : '',
          fullWidth ? sprinkles({ flex: 1 }) : '',
        )}
      >
        {textContent}
      </Tooltip>
    );
  },
);

Text.displayName = 'Text';
